import type { WebviewWindow as TauriWebviewWindow } from "@tauri-apps/api/webviewWindow";
import {
  APPLICATION_WINDOW_LABEL_PREFIX,
  currentWindowKind,
  type DesktopWindowKind,
  isTauriRuntime,
  SURFACE_WINDOW_LABEL_PREFIX,
} from "./environment";

const DETACHED_WINDOW_PATH = "/desktop-window";
const LAUNCH_STORAGE_PREFIX = "fraction:detached-window:";
const INSTANCE_WINDOW_STORAGE_PREFIX = "fraction:detached-window-instance:";
const WARM_WINDOW_STORAGE_PREFIX = "fraction:detached-window-warm:";
const MAX_STORED_LAUNCH_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const RETURN_TO_MAIN_EVENT_PREFIX = "fraction:detached-window:return-to-main";
const RETURN_TO_MAIN_ACK_PREFIX = "fraction:detached-window:return-to-main-ack";
const warmingWindowPromises = new Map<DesktopWindowKind, Promise<void>>();

/**
 * The surface a warm window shows before it is claimed: none.
 *
 * A warm window is not warmed *for* a surface. What costs the time is booting the webview — the
 * bundle, the React root, LaunchDarkly, auth, the query cache — and none of that differs by
 * surface. So one window waits blank and becomes whichever surface asks for it next, resized and
 * retitled on the way out. What it cannot change is its kind: that is read from its label, and
 * Tauri labels are fixed at creation. Hence one warm window per kind rather than per surface.
 */
export const WARM_SURFACE = "warm";

/** Until a claiming surface renames it. */
const WARM_WINDOW_TITLE = "Fraction LOS";

/**
 * The chrome every window in this app shares: the app draws its own header and reserves space for
 * the traffic lights, which lands wrong under any other title bar style.
 *
 * The default rather than something each surface repeats — and it has to be shared, because a warm
 * window is created before anyone knows which surface will claim it, and size and title are the
 * only things Tauri can change afterwards. `trafficLightPosition` in particular is creation-only,
 * so a surface that wanted different chrome could never be served warm.
 */
const SHARED_WINDOW_CHROME = {
  titleBarStyle: "overlay",
  hiddenTitle: true,
  trafficLightPosition: { x: 16, y: 28 },
} as const;

export interface DetachedWindowLaunch<TPayload = unknown> {
  surface: string;
  windowKey: string;
  payload: TPayload;
  createdAt: number;
}

export interface DetachedWindowOptions<TPayload> {
  /** Registry key rendered by DetachedWindowRoute. */
  surface: string;
  /**
   * What the window that hosts this surface IS — see `currentWindowKind`.
   *
   * Surfaces are single-purpose windows and that is the default. `application` is for a surface
   * whose whole job is to become the application, so the window must not be mistaken for one
   * showing one thing.
   */
  kind?: DesktopWindowKind;
  /** Stable logical identity, e.g. `application:<id>` or `document:<id>`. */
  instanceKey: string;
  payload: TPayload;
  title: string;
  width: number;
  height: number;
  minWidth?: number;
  minHeight?: number;
  /** Optional native chrome treatment for surfaces that render their own header. */
  titleBarStyle?: "visible" | "transparent" | "overlay";
  hiddenTitle?: boolean;
  trafficLightPosition?: { x: number; y: number };
  /**
   * `beside-main` prefers an adjacent companion position when the main window's monitor has room.
   * `cascade` offsets each new window of the surface from the ones already open, so surfaces that
   * open many windows (one per document) don't stack them all on the centred default position.
   */
  placement?: "beside-main" | "cascade";
  /** Show the window without activating it when work was dispatched automatically. */
  focus?: boolean;
}

export type DetachedWindowPrewarmOptions = Omit<DetachedWindowOptions<never>, "instanceKey" | "payload">;

interface DetachedWindowReturnRequest<TPayload> {
  requestId: string;
  sourceWindowLabel: string;
  payload: TPayload;
}

interface DetachedWindowReturnAcknowledgement {
  requestId: string;
  ok: boolean;
  error?: string;
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function safeSegment(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 24) || "surface"
  );
}

function returnToMainEvent(surface: string): string {
  return `${RETURN_TO_MAIN_EVENT_PREFIX}:${safeSegment(surface)}`;
}

function returnToMainAckEvent(requestId: string): string {
  return `${RETURN_TO_MAIN_ACK_PREFIX}:${requestId}`;
}

function instanceWindowStorageKey(windowKey: string): string {
  return `${INSTANCE_WINDOW_STORAGE_PREFIX}${windowKey}`;
}

function warmWindowStorageKey(kind: DesktopWindowKind): string {
  return `${WARM_WINDOW_STORAGE_PREFIX}${kind}`;
}

export function currentWindowTheme(): "light" | "dark" {
  if (document.documentElement.classList.contains("dark")) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function windowBackgroundColor(theme: "light" | "dark"): string {
  return theme === "dark" ? "#111214" : "#ffffff";
}

interface WindowRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MonitorWorkArea extends WindowRectangle {
  scaleFactor: number;
}

export function findCompanionWindowPosition({
  main,
  target,
  targetScaleFactor,
  currentMonitor,
  alternateMonitors,
}: {
  main: WindowRectangle;
  target: Pick<WindowRectangle, "width" | "height">;
  targetScaleFactor: number;
  currentMonitor: MonitorWorkArea;
  alternateMonitors: MonitorWorkArea[];
}): { x: number; y: number } | null {
  const logicalTarget = {
    width: target.width / targetScaleFactor,
    height: target.height / targetScaleFactor,
  };
  const targetSizeOn = (monitor: MonitorWorkArea) => ({
    width: Math.round(logicalTarget.width * monitor.scaleFactor),
    height: Math.round(logicalTarget.height * monitor.scaleFactor),
  });
  const clamp = (value: number, minimum: number, maximum: number) =>
    Math.min(Math.max(value, minimum), maximum);
  const overlapsMain = (position: { x: number; y: number }, size: { width: number; height: number }) =>
    position.x < main.x + main.width &&
    position.x + size.width > main.x &&
    position.y < main.y + main.height &&
    position.y + size.height > main.y;

  const gap = Math.round(16 * currentMonitor.scaleFactor);
  const currentTarget = targetSizeOn(currentMonitor);
  const workRight = currentMonitor.x + currentMonitor.width;
  const workBottom = currentMonitor.y + currentMonitor.height;
  const rightX = main.x + main.width + gap;
  const leftX = main.x - currentTarget.width - gap;
  const currentX =
    rightX + currentTarget.width <= workRight ? rightX : leftX >= currentMonitor.x ? leftX : undefined;
  if (currentX !== undefined) {
    return {
      x: currentX,
      y: Math.max(
        currentMonitor.y,
        Math.min(Math.max(main.y, currentMonitor.y), workBottom - currentTarget.height)
      ),
    };
  }

  const mainCenterX = main.x + main.width / 2;
  const mainCenterY = main.y + main.height / 2;
  const alternates = alternateMonitors
    .map((monitor) => ({ monitor, target: targetSizeOn(monitor) }))
    .sort((first, second) => {
      const firstX = first.monitor.x + first.monitor.width / 2 - mainCenterX;
      const firstY = first.monitor.y + first.monitor.height / 2 - mainCenterY;
      const secondX = second.monitor.x + second.monitor.width / 2 - mainCenterX;
      const secondY = second.monitor.y + second.monitor.height / 2 - mainCenterY;
      return firstX * firstX + firstY * firstY - (secondX * secondX + secondY * secondY);
    });

  const fittingAlternates = alternates.filter(({ monitor, target: alternateTarget }) => {
    const monitorGap = Math.round(16 * monitor.scaleFactor);
    return (
      alternateTarget.width + monitorGap * 2 <= monitor.width &&
      alternateTarget.height + monitorGap * 2 <= monitor.height
    );
  });
  for (const { monitor, target: alternateTarget } of fittingAlternates) {
    const alternateGap = Math.round(16 * monitor.scaleFactor);
    const minX = monitor.x + alternateGap;
    const maxX = monitor.x + monitor.width - alternateTarget.width - alternateGap;
    const minY = monitor.y + alternateGap;
    const maxY = monitor.y + monitor.height - alternateTarget.height - alternateGap;
    const monitorCenterX = monitor.x + monitor.width / 2;
    const monitorCenterY = monitor.y + monitor.height / 2;
    const opensToRight = monitorCenterX >= mainCenterX;
    const preferred = {
      x: opensToRight ? minX : maxX,
      y: clamp(main.y, minY, maxY),
    };
    if (!overlapsMain(preferred, alternateTarget)) return preferred;

    const horizontal = {
      x: opensToRight
        ? Math.max(minX, main.x + main.width + alternateGap)
        : Math.min(maxX, main.x - alternateTarget.width - alternateGap),
      y: preferred.y,
    };
    if (horizontal.x >= minX && horizontal.x <= maxX && !overlapsMain(horizontal, alternateTarget)) {
      return horizontal;
    }

    const opensBelow = monitorCenterY >= mainCenterY;
    const vertical = {
      x: clamp(main.x, minX, maxX),
      y: opensBelow
        ? Math.max(minY, main.y + main.height + alternateGap)
        : Math.min(maxY, main.y - alternateTarget.height - alternateGap),
    };
    if (vertical.y >= minY && vertical.y <= maxY && !overlapsMain(vertical, alternateTarget)) {
      return vertical;
    }
  }

  const fallback = alternates[0];
  if (fallback) {
    const { monitor, target: alternateTarget } = fallback;
    const fallbackGap = Math.round(16 * monitor.scaleFactor);
    const fitsWidth = alternateTarget.width + fallbackGap * 2 <= monitor.width;
    const fitsHeight = alternateTarget.height + fallbackGap * 2 <= monitor.height;
    const monitorCenterX = monitor.x + monitor.width / 2;
    const opensToRight = monitorCenterX >= mainCenterX;
    const minX = monitor.x + (fitsWidth ? fallbackGap : 0);
    const maxX = monitor.x + monitor.width - alternateTarget.width - (fitsWidth ? fallbackGap : 0);
    const beyondMainX = opensToRight
      ? main.x + main.width + fallbackGap
      : main.x - alternateTarget.width - fallbackGap;
    const x = fitsWidth ? clamp(beyondMainX, minX, maxX) : monitor.x;
    const minY = monitor.y + (fitsHeight ? fallbackGap : 0);
    const maxY = monitor.y + monitor.height - alternateTarget.height - (fitsHeight ? fallbackGap : 0);
    return {
      x,
      y: fitsHeight ? clamp(main.y, minY, maxY) : monitor.y,
    };
  }

  return {
    x:
      currentTarget.width + gap <= currentMonitor.width
        ? workRight - currentTarget.width - gap
        : currentMonitor.x,
    y:
      currentTarget.height + gap <= currentMonitor.height
        ? workBottom - currentTarget.height - gap
        : currentMonitor.y,
  };
}

export function findMonitorForWindow(
  windowRectangle: WindowRectangle,
  monitors: MonitorWorkArea[]
): MonitorWorkArea | null {
  const windowCenterX = windowRectangle.x + windowRectangle.width / 2;
  const windowCenterY = windowRectangle.y + windowRectangle.height / 2;
  return (
    [...monitors].sort((first, second) => {
      const overlapArea = (monitor: MonitorWorkArea) => {
        const width = Math.max(
          0,
          Math.min(windowRectangle.x + windowRectangle.width, monitor.x + monitor.width) -
            Math.max(windowRectangle.x, monitor.x)
        );
        const height = Math.max(
          0,
          Math.min(windowRectangle.y + windowRectangle.height, monitor.y + monitor.height) -
            Math.max(windowRectangle.y, monitor.y)
        );
        return width * height;
      };
      const overlapDifference = overlapArea(second) - overlapArea(first);
      if (overlapDifference !== 0) return overlapDifference;
      const distance = (monitor: MonitorWorkArea) => {
        const x = monitor.x + monitor.width / 2 - windowCenterX;
        const y = monitor.y + monitor.height / 2 - windowCenterY;
        return x * x + y * y;
      };
      return distance(first) - distance(second);
    })[0] ?? null
  );
}

const CASCADE_OFFSET = 32;

/**
 * Offset a new window from the deepest window already open on the same surface. Every Tauri window
 * is centred on creation, so without this a surface that opens one window per document buries each
 * new window exactly on top of the last.
 */
export function findCascadedWindowPosition({
  anchors,
  target,
  monitors,
}: {
  /** Outer positions of the surface's already-visible windows. */
  anchors: { x: number; y: number }[];
  target: Pick<WindowRectangle, "width" | "height">;
  monitors: MonitorWorkArea[];
}): { x: number; y: number } | null {
  const deepest = anchors.reduce<{ x: number; y: number } | null>(
    (furthest, candidate) =>
      !furthest || candidate.x + candidate.y > furthest.x + furthest.y ? candidate : furthest,
    null
  );
  if (!deepest) return null;

  const monitor = findMonitorForWindow({ ...deepest, ...target }, monitors);
  if (!monitor) return null;

  const step = Math.round(CASCADE_OFFSET * monitor.scaleFactor);
  const next = { x: deepest.x + step, y: deepest.y + step };
  if (
    next.x + target.width <= monitor.x + monitor.width &&
    next.y + target.height <= monitor.y + monitor.height
  ) {
    return next;
  }

  // The run walked off the bottom-right of the work area — restart it from the top-left corner.
  return {
    x: Math.max(monitor.x, Math.min(monitor.x + step, monitor.x + monitor.width - target.width)),
    y: Math.max(monitor.y, Math.min(monitor.y + step, monitor.y + monitor.height - target.height)),
  };
}

export async function revealWindow(
  target: Pick<TauriWebviewWindow, "label" | "setFocus" | "show" | "unminimize">,
  place?: () => Promise<void>,
  focus = true
): Promise<void> {
  await place?.();
  if (focus) {
    await target.show();
    await target.unminimize();
  } else {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("show_window_without_focus", { label: target.label });
  }
  await place?.();
  if (focus) await target.setFocus();
}

function toMonitorWorkArea(monitor: {
  workArea: { position: { x: number; y: number }; size: { width: number; height: number } };
  scaleFactor: number;
}): MonitorWorkArea {
  return {
    x: monitor.workArea.position.x,
    y: monitor.workArea.position.y,
    width: monitor.workArea.size.width,
    height: monitor.workArea.size.height,
    scaleFactor: monitor.scaleFactor,
  };
}

/**
 * Offset this window from the ones `isSibling` accepts instead of stacking on the centre.
 * Exported so full application windows can cascade among themselves too.
 */
export async function placeWindowCascaded(
  target: Pick<TauriWebviewWindow, "label" | "outerSize" | "setPosition">,
  isSibling: (label: string) => boolean
): Promise<void> {
  const [{ getAllWebviewWindows }, { PhysicalPosition }, { availableMonitors }] = await Promise.all([
    import("@tauri-apps/api/webviewWindow"),
    import("@tauri-apps/api/dpi"),
    import("@tauri-apps/api/window"),
  ]);
  const siblings = (await getAllWebviewWindows()).filter(
    (candidate) => candidate.label !== target.label && isSibling(candidate.label)
  );
  // Hidden windows are prewarmed slots nobody can see — they never anchor the cascade.
  const anchors = (
    await Promise.all(
      siblings.map(async (sibling) =>
        (await sibling.isVisible().catch(() => false)) ? await sibling.outerPosition() : null
      )
    )
  ).filter((position): position is NonNullable<typeof position> => !!position);
  if (!anchors.length) return;

  const [targetSize, monitors] = await Promise.all([target.outerSize(), availableMonitors()]);
  const position = findCascadedWindowPosition({
    anchors,
    target: targetSize,
    monitors: monitors.map(toMonitorWorkArea),
  });
  if (position) await target.setPosition(new PhysicalPosition(position.x, position.y));
}

/** Offset this window from the surface's other open windows instead of stacking on the centre. */
function placeCascadedWindow(
  target: Pick<TauriWebviewWindow, "label" | "outerSize" | "setPosition">,
  kind: DesktopWindowKind
): Promise<void> {
  // Every window of this kind, not just this surface's: a claimed warm window keeps the label it
  // was created with, so its surface is no longer readable from it — and stepping a new window down
  // from whatever is already open is what the cascade was for anyway.
  const prefix = windowLabelPrefix(kind);
  return placeWindowCascaded(target, (label) => label.startsWith(prefix));
}

async function placeBesideMainWindow(
  target: Pick<TauriWebviewWindow, "outerSize" | "scaleFactor" | "setPosition">
): Promise<void> {
  const [{ WebviewWindow }, { PhysicalPosition }, { availableMonitors }] = await Promise.all([
    import("@tauri-apps/api/webviewWindow"),
    import("@tauri-apps/api/dpi"),
    import("@tauri-apps/api/window"),
  ]);
  const mainWindow = await WebviewWindow.getByLabel("main");
  if (!mainWindow) return;

  const [mainPosition, mainSize, targetSize, targetScaleFactor, monitors] = await Promise.all([
    mainWindow.outerPosition(),
    mainWindow.outerSize(),
    target.outerSize(),
    target.scaleFactor(),
    availableMonitors(),
  ]);
  const main = { x: mainPosition.x, y: mainPosition.y, width: mainSize.width, height: mainSize.height };
  const monitorWorkAreas = monitors.map(toMonitorWorkArea);
  const currentMonitor = findMonitorForWindow(main, monitorWorkAreas);
  if (!currentMonitor) return;
  const position = findCompanionWindowPosition({
    main,
    target: targetSize,
    targetScaleFactor,
    currentMonitor,
    alternateMonitors: monitorWorkAreas.filter((candidate) => candidate !== currentMonitor),
  });
  if (position) await target.setPosition(new PhysicalPosition(position.x, position.y));
}

function clearWindowMappings(windowLabel: string): void {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(INSTANCE_WINDOW_STORAGE_PREFIX) && localStorage.getItem(key) === windowLabel) {
      localStorage.removeItem(key);
    }
  }
}

/** Windows say what they are with their label, so the prefix is chosen from the kind. */
function windowLabelPrefix(kind: DesktopWindowKind): string {
  return kind === "application" ? APPLICATION_WINDOW_LABEL_PREFIX : SURFACE_WINDOW_LABEL_PREFIX;
}

export function detachedWindowKey(
  surface: string,
  instanceKey: string,
  kind: DesktopWindowKind = "surface"
): string {
  return `${windowLabelPrefix(kind)}${safeSegment(surface)}-${hashString(`${surface}:${instanceKey}`)}`;
}

export function detachedWindowLaunchStorageKey(windowKey: string): string {
  return `${LAUNCH_STORAGE_PREFIX}${windowKey}`;
}

export async function closeDetachedWindow(surface: string, instanceKey: string): Promise<boolean> {
  if (!isTauriRuntime()) return false;
  const { WebviewWindow } = await import("@tauri-apps/api/webviewWindow");
  const logicalWindowKey = detachedWindowKey(surface, instanceKey);
  const windowKey = localStorage.getItem(instanceWindowStorageKey(logicalWindowKey)) || logicalWindowKey;
  const detached = await WebviewWindow.getByLabel(windowKey);
  if (!detached) {
    localStorage.removeItem(instanceWindowStorageKey(logicalWindowKey));
    return false;
  }
  await detached.destroy();
  localStorage.removeItem(detachedWindowLaunchStorageKey(windowKey));
  clearWindowMappings(windowKey);
  return true;
}

/**
 * Hand a detached surface back to its main-window host and wait until the host
 * confirms that it has adopted the payload. The handshake is surface-agnostic
 * so document workspaces can use the same lifecycle later.
 */
export async function requestDetachedWindowReturnToMain<TPayload>(
  surface: string,
  payload: TPayload,
  options: { preserveSourceWindow?: boolean } = {}
): Promise<void> {
  if (!isTauriRuntime()) throw new Error("Detached windows are only available in the desktop app.");

  const [{ emitTo, listen }, { getCurrentWindow }] = await Promise.all([
    import("@tauri-apps/api/event"),
    import("@tauri-apps/api/window"),
  ]);
  const sourceWindowLabel = getCurrentWindow().label;
  const requestId = crypto.randomUUID();
  const acknowledgementEvent = returnToMainAckEvent(requestId);
  let resolveAcknowledgement: (value: DetachedWindowReturnAcknowledgement) => void = () => undefined;
  const acknowledgement = new Promise<DetachedWindowReturnAcknowledgement>((resolve) => {
    resolveAcknowledgement = resolve;
  });
  const unlisten = await listen<DetachedWindowReturnAcknowledgement>(acknowledgementEvent, (event) => {
    if (event.payload.requestId === requestId) resolveAcknowledgement(event.payload);
  });
  const timeout = setTimeout(
    () => resolveAcknowledgement({ requestId, ok: false, error: "The main window did not respond." }),
    5_000
  );

  try {
    await emitTo<DetachedWindowReturnRequest<TPayload>>("main", returnToMainEvent(surface), {
      requestId,
      sourceWindowLabel,
      payload,
    });
    const result = await acknowledgement;
    if (!result.ok) throw new Error(result.error || "The main window could not restore this surface.");
    if (!options.preserveSourceWindow) {
      localStorage.removeItem(detachedWindowLaunchStorageKey(sourceWindowLabel));
      clearWindowMappings(sourceWindowLabel);
    }
  } finally {
    clearTimeout(timeout);
    unlisten();
  }
}

/** Register the main-window adopter for a detached surface. */
export async function listenForDetachedWindowReturns<TPayload>(
  surface: string,
  handler: (payload: TPayload) => void | Promise<void>
): Promise<() => void> {
  if (!isTauriRuntime()) return () => undefined;
  const { emitTo, listen } = await import("@tauri-apps/api/event");
  return listen<DetachedWindowReturnRequest<TPayload>>(returnToMainEvent(surface), (event) => {
    const request = event.payload;
    void Promise.resolve()
      .then(() => handler(request.payload))
      .then(
        () =>
          emitTo<DetachedWindowReturnAcknowledgement>(
            request.sourceWindowLabel,
            returnToMainAckEvent(request.requestId),
            { requestId: request.requestId, ok: true }
          ),
        (error) =>
          emitTo<DetachedWindowReturnAcknowledgement>(
            request.sourceWindowLabel,
            returnToMainAckEvent(request.requestId),
            { requestId: request.requestId, ok: false, error: String(error) }
          )
      );
  });
}

function pruneExpiredLaunches(now = Date.now()): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(LAUNCH_STORAGE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { createdAt?: number };
      if (!parsed.createdAt || now - parsed.createdAt > MAX_STORED_LAUNCH_AGE_MS) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Window launching still works when storage enumeration is unavailable.
  }
}

/**
 * The launch record for a window, whichever surface wrote it.
 *
 * A warm window is claimed by a surface it could not have known at boot, so it reads what arrived
 * rather than checking it against the surface in its own URL.
 */
export function readAnyDetachedWindowLaunch<TPayload = unknown>(
  windowKey: string
): DetachedWindowLaunch<TPayload> | null {
  try {
    const raw = localStorage.getItem(detachedWindowLaunchStorageKey(windowKey));
    if (!raw) return null;
    const launch = JSON.parse(raw) as DetachedWindowLaunch<TPayload>;
    if (launch.windowKey !== windowKey) return null;
    if (!launch.createdAt || Date.now() - launch.createdAt > MAX_STORED_LAUNCH_AGE_MS) return null;
    return launch;
  } catch {
    return null;
  }
}

export function readDetachedWindowLaunch<TPayload = unknown>(
  surface: string,
  windowKey: string
): DetachedWindowLaunch<TPayload> | null {
  try {
    const raw = localStorage.getItem(detachedWindowLaunchStorageKey(windowKey));
    if (!raw) return null;
    const launch = JSON.parse(raw) as DetachedWindowLaunch<TPayload>;
    if (launch.surface !== surface || launch.windowKey !== windowKey) return null;
    if (!launch.createdAt || Date.now() - launch.createdAt > MAX_STORED_LAUNCH_AGE_MS) return null;
    return launch;
  } catch {
    return null;
  }
}

function writeDetachedWindowLaunch<TPayload>(
  surface: string,
  windowKey: string,
  payload: TPayload
): DetachedWindowLaunch<TPayload> {
  const launch: DetachedWindowLaunch<TPayload> = {
    surface,
    windowKey,
    payload,
    createdAt: Date.now(),
  };
  pruneExpiredLaunches(launch.createdAt);
  localStorage.setItem(detachedWindowLaunchStorageKey(windowKey), JSON.stringify(launch));
  return launch;
}

/**
 * Boot one hidden WebView for a surface. Opening consumes that slot and starts
 * warming its replacement, so the expensive app/provider startup happens off
 * the user's click path. Surfaces opt in explicitly.
 */
export function prewarmDetachedWindow(options: DetachedWindowPrewarmOptions): Promise<void> {
  return prewarmWindow(options.kind ?? "surface");
}

/**
 * Boot one hidden window of `kind` so the next open is a reveal rather than a cold start.
 *
 * One per kind, shared by every surface of that kind — see `WARM_SURFACE`. Opening consumes the
 * slot and starts its replacement, so the boot happens off the click path.
 */
export function prewarmWindow(kind: DesktopWindowKind = "surface"): Promise<void> {
  if (currentWindowKind() === "surface") return Promise.resolve();
  if (!isTauriRuntime()) return Promise.resolve();

  const existingWarmup = warmingWindowPromises.get(kind);
  if (existingWarmup) return existingWarmup;

  const warmup = createWarmWindow(kind).finally(() => {
    if (warmingWindowPromises.get(kind) === warmup) warmingWindowPromises.delete(kind);
  });
  warmingWindowPromises.set(kind, warmup);
  return warmup;
}

async function createWarmWindow(kind: DesktopWindowKind): Promise<void> {
  const [{ WebviewWindow }, { LogicalPosition }] = await Promise.all([
    import("@tauri-apps/api/webviewWindow"),
    import("@tauri-apps/api/dpi"),
  ]);
  const slotKey = warmWindowStorageKey(kind);
  const recordedLabel = localStorage.getItem(slotKey);
  if (recordedLabel && (await WebviewWindow.getByLabel(recordedLabel))) return;
  if (recordedLabel) localStorage.removeItem(slotKey);

  const windowKey = `${windowLabelPrefix(kind)}warm-${hashString(crypto.randomUUID())}`;
  localStorage.setItem(slotKey, windowKey);
  const params = new URLSearchParams({ windowKey, prewarm: "1" });
  const theme = currentWindowTheme();
  const warmed = new WebviewWindow(windowKey, {
    url: `${DETACHED_WINDOW_PATH}/${WARM_SURFACE}?${params}`,
    title: WARM_WINDOW_TITLE,
    // Resized by whichever surface claims it; these only decide what it would look like if shown.
    width: 900,
    height: 700,
    resizable: true,
    decorations: true,
    titleBarStyle: SHARED_WINDOW_CHROME.titleBarStyle,
    hiddenTitle: SHARED_WINDOW_CHROME.hiddenTitle,
    trafficLightPosition: new LogicalPosition(
      SHARED_WINDOW_CHROME.trafficLightPosition.x,
      SHARED_WINDOW_CHROME.trafficLightPosition.y
    ),
    visible: false,
    focus: false,
    theme,
    backgroundColor: windowBackgroundColor(theme),
  });

  await new Promise<void>((resolve, reject) => {
    void warmed.once("tauri://created", () => resolve());
    void warmed.once<{ message?: string }>("tauri://error", (event) => {
      localStorage.removeItem(slotKey);
      reject(new Error(event.payload?.message || "Failed to prewarm a window."));
    });
  });
}

/** Give a claimed warm window the size the surface asked for. */
async function applyClaimedWindowSize(
  target: Pick<TauriWebviewWindow, "setSize" | "setMinSize">,
  {
    width,
    height,
    minWidth,
    minHeight,
  }: Pick<DetachedWindowOptions<never>, "width" | "height" | "minWidth" | "minHeight">
): Promise<void> {
  const { LogicalSize } = await import("@tauri-apps/api/dpi");
  if (minWidth !== undefined || minHeight !== undefined) {
    await target.setMinSize(new LogicalSize(minWidth ?? 0, minHeight ?? 0));
  }
  await target.setSize(new LogicalSize(width, height));
}

/**
 * Open or focus a native Tauri window hosting a registered detached surface.
 * Payload transport and window lifecycle are deliberately surface-agnostic so
 * document workspaces and future tools can reuse this without agent coupling.
 */
export async function openDetachedWindow<TPayload>({
  surface,
  kind = "surface",
  instanceKey,
  payload,
  title,
  width,
  height,
  minWidth,
  minHeight,
  titleBarStyle,
  hiddenTitle,
  trafficLightPosition,
  placement,
  focus = true,
}: DetachedWindowOptions<TPayload>): Promise<string> {
  if (!isTauriRuntime()) throw new Error("Detached windows are only available in the desktop app.");

  const [{ WebviewWindow }, { LogicalPosition }] = await Promise.all([
    import("@tauri-apps/api/webviewWindow"),
    import("@tauri-apps/api/dpi"),
  ]);
  // A window the user already dragged somewhere keeps its position; only freshly created (or
  // freshly revealed warm) windows get placed.
  const placeNewWindow = (target: TauriWebviewWindow): (() => Promise<void>) | undefined => {
    if (placement === "beside-main") return () => placeBesideMainWindow(target);
    if (placement === "cascade") return () => placeCascadedWindow(target, kind);
    return undefined;
  };
  const logicalWindowKey = detachedWindowKey(surface, instanceKey, kind);
  const mappedWindowKey = localStorage.getItem(instanceWindowStorageKey(logicalWindowKey));
  let windowKey = mappedWindowKey || logicalWindowKey;
  let existing = await WebviewWindow.getByLabel(windowKey);
  if (mappedWindowKey && !existing) {
    localStorage.removeItem(instanceWindowStorageKey(logicalWindowKey));
    windowKey = logicalWindowKey;
    existing = await WebviewWindow.getByLabel(windowKey);
  }
  if (existing) {
    writeDetachedWindowLaunch(surface, windowKey, payload);
    await existing.setTitle(title);
    await revealWindow(
      existing,
      placement === "beside-main" ? () => placeBesideMainWindow(existing) : undefined,
      focus
    );
    return windowKey;
  }

  /**
   * Warm, open, warm again.
   *
   * There is one warm window per kind and every open consumes it, so every open starts the
   * replacement — whichever path handed the window over, and whichever surface asked for it.
   */
  const replenishWarmWindow = () => {
    void prewarmWindow(kind).catch((error) =>
      console.warn(`Could not replenish the warm ${kind} window.`, error)
    );
  };

  const pendingWarmup = warmingWindowPromises.get(kind);
  if (pendingWarmup) {
    await pendingWarmup.catch(() => undefined);
  }
  const warmSlotKey = warmWindowStorageKey(kind);
  const warmWindowKey = localStorage.getItem(warmSlotKey);
  const warmWindow = warmWindowKey ? await WebviewWindow.getByLabel(warmWindowKey) : null;
  if (warmWindowKey) localStorage.removeItem(warmSlotKey);
  if (warmWindowKey && warmWindow) {
    windowKey = warmWindowKey;
    localStorage.setItem(instanceWindowStorageKey(logicalWindowKey), windowKey);
    // Sized and titled before the payload lands, so the surface renders into the window it asked
    // for rather than resizing under the person's eyes.
    await applyClaimedWindowSize(warmWindow, { width, height, minWidth, minHeight });
    await warmWindow.setTitle(title);
    writeDetachedWindowLaunch(surface, windowKey, payload);
    await revealWindow(warmWindow, placeNewWindow(warmWindow), focus);
    replenishWarmWindow();
    return windowKey;
  }

  writeDetachedWindowLaunch(surface, windowKey, payload);

  const params = new URLSearchParams({ windowKey });
  const theme = currentWindowTheme();
  const chrome = {
    titleBarStyle: titleBarStyle ?? SHARED_WINDOW_CHROME.titleBarStyle,
    hiddenTitle: hiddenTitle ?? SHARED_WINDOW_CHROME.hiddenTitle,
    trafficLightPosition: trafficLightPosition ?? SHARED_WINDOW_CHROME.trafficLightPosition,
  };
  const detached = new WebviewWindow(windowKey, {
    url: `${DETACHED_WINDOW_PATH}/${encodeURIComponent(surface)}?${params}`,
    title,
    width,
    height,
    minWidth,
    minHeight,
    resizable: true,
    decorations: true,
    titleBarStyle: chrome.titleBarStyle,
    hiddenTitle: chrome.hiddenTitle,
    trafficLightPosition: new LogicalPosition(chrome.trafficLightPosition.x, chrome.trafficLightPosition.y),
    visible: placement ? false : undefined,
    focus,
    theme,
    backgroundColor: windowBackgroundColor(theme),
  });

  await new Promise<void>((resolve, reject) => {
    void detached.once("tauri://created", () => resolve());
    void detached.once<{ message?: string }>("tauri://error", (event) => {
      reject(new Error(event.payload?.message || `Failed to open ${title}.`));
    });
  });
  // Placed surfaces are created hidden so they never flash at the default position first.
  if (placement) await revealWindow(detached, placeNewWindow(detached), focus);
  // Cold open: nothing was warm to claim, so start the one the next open will use.
  replenishWarmWindow();
  return windowKey;
}

/**
 * The key a surface window reads its launch payload under — not a test of what kind of window this
 * is. Ask `currentWindowKind()` for that: it answers in every window, including the ones that never
 * carry a payload.
 */
export function currentDetachedWindowKey(): string | null {
  if (typeof window === "undefined" || !window.location.pathname.startsWith(`${DETACHED_WINDOW_PATH}/`)) {
    return null;
  }
  return new URLSearchParams(window.location.search).get("windowKey");
}
